/**
 * Pitch's UI module.
 *
 * What Pitch has to say that 500 does not: the contract is a number of points
 * with no suit until the pitcher leads, a plain lead may be followed *or*
 * trumped, and the tricks matter only for the six scoring cards and the Game
 * pips inside them — so the felt names the Nine, Five and Jack when they fall.
 */
import type { ReactNode } from 'react'
import {
  pitchRanking,
  rankOf,
  suitOf,
  type CardId,
  type PitchContract,
  type PitchHandExtra,
  type RankingContext,
  type Seat,
  type Suit,
} from '@cards/shared'
import type { GameUi, LegalCall } from '../types'
import type { TableView } from '../../table/view'
import { Glyph } from '../../table/atoms'
import {
  CardWord, auctionCallsOf, auctionStripOf, handHint, readCalls, seatStatus, sortBySuit, winningSeat,
} from '../common'

const TRICKS = 6

const isRecord = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null

function contractOf(view: TableView): PitchContract | null {
  const c = view.hand?.contract
  return isRecord(c) && typeof c.pitcher === 'number' && typeof c.bid === 'number' ? (c as unknown as PitchContract) : null
}

function trumpOf(view: TableView): Suit | null {
  const extra = view.hand?.extra as Partial<PitchHandExtra> | undefined
  return extra?.trump ?? null
}

function bidOf(call: unknown): number | null {
  return isRecord(call) && call.type === 'bid' && typeof call.points === 'number' ? call.points : null
}

function highOf(view: TableView): { seat: Seat; points: number } | null {
  const high = (view.hand?.auction as { high?: unknown } | undefined)?.high
  return isRecord(high) && typeof high.seat === 'number' && typeof high.points === 'number'
    ? { seat: high.seat, points: high.points }
    : null
}

const contexts = new WeakMap<object, RankingContext>()
function rankingFor(view: TableView): RankingContext {
  const key = view.hand as object | null
  const hit = key ? contexts.get(key) : undefined
  if (hit) return hit
  const ctx = pitchRanking({ trump: trumpOf(view) })
  if (key) contexts.set(key, ctx)
  return ctx
}

/** The scoring cards a trick holds, named: `the Nine and the Jack`. */
function pointNames(cards: readonly CardId[], trump: Suit | null): string[] {
  if (trump === null) return []
  const names: Record<number, string> = { 9: 'the Nine', 5: 'the Five', 11: 'the Jack' }
  return cards
    .filter((c) => suitOf(c) === trump && names[rankOf(c) ?? 0])
    .sort((a, b) => [9, 5, 11].indexOf(rankOf(a) ?? 0) - [9, 5, 11].indexOf(rankOf(b) ?? 0))
    .map((c) => names[rankOf(c) ?? 0])
}

function andList(items: readonly string[]): string {
  return items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

function BidText({ points, trump }: { points: number; trump: Suit | null }): JSX.Element {
  return (
    <span>
      {points}
      {trump ? <Glyph suit={trump} /> : null}
    </span>
  )
}

export const pitchUi: GameUi = {
  id: 'pitch',
  name: 'Pitch',
  rotation: 'clockwise',
  options: [
    { key: 'target', label: 'Game to', choices: [52, 31, 21, 11].map((v) => ({ label: String(v), value: v })) },
    {
      key: 'lowGoesTo',
      label: 'Low scores for',
      choices: [{ label: 'Whoever takes it', value: 'taker' }, { label: 'Whoever held it', value: 'holder' }],
    },
  ],

  tricksPerHand: () => TRICKS,
  // The bid is in points, not tricks, so the track has no goal dot.
  tricksNeeded: () => null,

  contractReading: (view) => {
    const hand = view.hand
    if (!hand) return null
    const contract = contractOf(view)
    if (contract) {
      const trump = trumpOf(view)
      const mine = contract.pitcher === view.mySeat
      return {
        node: (
          <>
            <BidText points={contract.bid} trump={trump} />
            <span className="who">{mine ? 'You' : view.name(contract.pitcher)}</span>
          </>
        ),
        caption: trump ? 'points bid' : 'lead names trump',
      }
    }
    const high = highOf(view)
    if (!high) {
      return { node: <span className="faint">—</span>, caption: hand.phase === 'abandoned' ? 'passed out' : 'no bid yet' }
    }
    return {
      node: (
        <>
          <BidText points={high.points} trump={null} />
          <span className="who">{high.seat === view.mySeat ? 'You' : view.name(high.seat)}</span>
        </>
      ),
      caption: 'bid to beat',
    }
  },

  trackCaption: (view) => {
    const hand = view.hand
    if (!hand) return ''
    const us = view.trickDots.filter((d) => d === 'us').length
    const them = view.trickDots.filter((d) => d === 'them').length
    if (hand.phase === 'abandoned') return 'no tricks played'
    if (us + them === 0) return `${TRICKS} tricks to play`
    return `${us} of ${TRICKS}, them ${them}`
  },

  seatStatus: (view, seat) =>
    seatStatus(view, seat, contractOf(view)?.pitcher === seat ? ['pitcher'] : []),

  winningSeat,

  callout: (view) => {
    const trick = view.trick
    if (!trick || trick.plays.length === 0) return null
    const leading = trick.winner ?? trick.winning
    const card = trick.plays.find((p) => p.seat === leading)?.card ?? null
    if (leading === null || card === null) return null
    const name = view.name(leading)
    const points = pointNames(trick.plays.map((p) => p.card), trumpOf(view))
    const tail: ReactNode = points.length > 0 ? <>, and {andList(points)}</> : null
    if (trick.winner !== null) {
      return (
        <>
          <b>{name}</b> takes it with <CardWord card={card} />
          {tail}
        </>
      )
    }
    return (
      <>
        <b>{name}</b> has it with <CardWord card={card} />
        {tail}
      </>
    )
  },

  handHint: (view) => {
    const trump = trumpOf(view)
    if (view.isMyTurn && trump === null) {
      return (
        <>
          <b>Your lead</b> names trump
        </>
      )
    }
    const led = view.trick?.ledSuit ?? null
    // Only worth saying when trumping is actually one of the choices.
    const canTrump = trump !== null && [...view.legalPlays.keys()].some((c) => suitOf(c) === trump)
    const followTail = canTrump && led !== null && led !== trump
      ? <> or trump <Glyph suit={trump!} /></>
      : null
    return handHint(view, { followTail })
  },

  cardMarks: (view, card) => {
    const ctx = rankingFor(view)
    const trump = ctx.isTrump(card)
    const rank = rankOf(card)
    return { trump, stamp: trump && rank === 9 ? '9 pts' : trump && rank === 5 ? '5 pts' : null }
  },

  sortHand: (view, cards) => sortBySuit(cards, rankingFor(view)),

  handResult: (view) => {
    const result = view.hand?.result
    const contract = contractOf(view)
    if (view.hand?.phase === 'abandoned') {
      return { headline: 'Thrown in', sentence: 'Nobody bid, so the cards go back and the deal moves on.' }
    }
    if (!result || !contract || !view.match) return null
    const team = view.match.table.teamOf[contract.pitcher]
    const mine = team === view.myTeam
    // A set side's only line is the setback, which names what it took.
    const setback = result.lines.find((l) => l.team === team && l.points < 0)
    const took = setback
      ? Number(/took (\d+)/.exec(setback.text)?.[1] ?? 0)
      : result.lines.filter((l) => l.team === team).reduce((n, l) => n + l.points, 0)
    const who = mine ? (contract.pitcher === view.mySeat ? 'You' : `You and ${view.name(contract.pitcher)}`) : view.name(contract.pitcher)
    if (result.contractMade) {
      return {
        headline: mine ? 'Made it' : 'They made it',
        sentence: `${who} bid ${contract.bid} and took ${took}.`,
      }
    }
    return {
      headline: mine ? 'Set back' : 'Set them',
      sentence: `${who} bid ${contract.bid} and took ${took}, so ${contract.bid} comes off.`,
    }
  },

  contractLabelNode: (label) => {
    const m = /^(\d+)([♠♥♦♣])$/.exec(label)
    if (!m) return <>{label}</>
    const suit = ({ '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' } as const)[m[2] as '♠']
    return <BidText points={Number(m[1])} trump={suit} />
  },

  auctionRule: 'Everyone bids once, dealer last. The dealer may take the bid by matching it.',

  auctionStrip: (view) =>
    auctionStripOf(
      view,
      readCalls(view.hand?.auction),
      (call) => {
        const points = bidOf(call)
        return points === null ? { label: 'pass', pass: true } : { label: String(points), pass: false }
      },
      highOf(view)?.seat ?? null,
    ),

  auctionCalls: (view) =>
    auctionCallsOf(
      view,
      readCalls(view.hand?.auction),
      (call) => {
        const points = bidOf(call)
        return points === null ? { label: 'pass', pass: true } : { label: String(points), pass: false }
      },
      highOf(view)?.seat ?? null,
    ),

  isPass: (call: LegalCall) => isRecord(call.call) && call.call.type === 'pass',

  explainHeadline: (call) => {
    const points = bidOf(call.call)
    return points === null ? <>{call.label}</> : <>Bid {points}</>
  },

  bidButtonLabel: (call) => {
    const points = bidOf(call.call)
    return points === null ? <>{call.label}</> : <>Bid {points}</>
  },

  decisionLayout: () => 'sheet',
  decisionHint: () => null,
  decisionWaiting: () => 'to choose',
}
